import React, { useState } from 'react';
import { ControlButton } from 'reactflow';
import {
  Popover,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Stack,
  Divider
} from '@mui/material';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import InputIcon from '@mui/icons-material/Input';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';

interface Shortcut {
  keys: string[];
  description: string;
  icon: React.ReactNode;
  isCreateNode: boolean;
}

const shortcuts: Shortcut[] = [
  {
    keys: ['R'],
    description: 'Respond to user',
    icon: <SmartToyIcon color='primary' />,
    isCreateNode: true
  },
  {
    keys: ['C'],
    description: 'Collect variable',
    icon: <InputIcon color='primary' />,
    isCreateNode: true
  },
  {
    keys: ['F'],
    description: 'Follow up options',
    icon: <ViewCarouselIcon color='primary' />,
    isCreateNode: true
  },
  {
    keys: ['G'],
    description: 'Go to question',
    icon: <QuestionMarkIcon color='primary' />,
    isCreateNode: true
  },
  {
    keys: ['V'],
    description: 'Variable comparison',
    icon: <FilterAltIcon color='primary' />,
    isCreateNode: true
  },
  {
    keys: ['D or Delete'],
    description: 'Delete selected node / node connection',
    icon: <DeleteIcon color='primary' />,
    isCreateNode: false
  },
  {
    keys: ['Esc'],
    description: 'Reset highlighting',
    icon: <CloseIcon color='primary' />,
    isCreateNode: false
  }
];

function KeyboardShortcuts() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const createNodeShortcuts = shortcuts.filter(
    (shortcut) => shortcut.isCreateNode
  );
  const otherShortcuts = shortcuts.filter((shortcut) => !shortcut.isCreateNode);

  return (
    <>
      <ControlButton onClick={handleClick}>
        <KeyboardIcon />
      </ControlButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        PaperProps={{
          sx: {
            maxWidth: 350,
            p: 2
          }
        }}
      >
        <Box>
          <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
            Keyboard Shortcuts
          </Typography>

          {/* Create Node Shortcuts */}
          <Typography
            variant='subtitle1'
            sx={{ mb: 1, fontWeight: 500, color: 'primary.main' }}
          >
            Create Nodes
          </Typography>
          <List sx={{ p: 0, mb: 1 }}>
            {createNodeShortcuts.map((shortcut, index) => (
              <ListItem key={index} sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {shortcut.icon}
                </ListItemIcon>
                <ListItemText
                  primary={shortcut.description}
                  sx={{
                    mr: 2,
                    '& .MuiListItemText-primary': {
                      fontSize: '1rem'
                    }
                  }}
                />
                <Stack direction='row' spacing={0.5}>
                  {shortcut.keys.map((key: string, keyIndex: number) => (
                    <Chip
                      key={keyIndex}
                      label={key}
                      size='small'
                      variant='outlined'
                      sx={{
                        fontSize: '0.75rem',
                        height: 24,
                        fontFamily: 'monospace',
                        backgroundColor: 'grey.100',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    />
                  ))}
                </Stack>
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          {/* Other Shortcuts */}
          <Typography
            variant='subtitle1'
            sx={{ mb: 1, fontWeight: 500, color: 'primary.main' }}
          >
            Actions
          </Typography>
          <List sx={{ p: 0 }}>
            {otherShortcuts.map((shortcut, index) => (
              <ListItem key={index} sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {shortcut.icon}
                </ListItemIcon>
                <ListItemText
                  primary={shortcut.description}
                  sx={{
                    mr: 2,
                    '& .MuiListItemText-primary': {
                      fontSize: '1rem'
                    }
                  }}
                />
                <Stack direction='row' spacing={0.5}>
                  {shortcut.keys.map((key: string, keyIndex: number) => (
                    <Chip
                      key={keyIndex}
                      label={key}
                      size='small'
                      variant='outlined'
                      sx={{
                        fontSize: '0.75rem',
                        height: 24,
                        fontFamily: 'monospace',
                        backgroundColor: 'grey.100',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    />
                  ))}
                </Stack>
              </ListItem>
            ))}
          </List>

          <Typography
            variant='caption'
            sx={{ color: 'text.secondary', mt: 1, display: 'block' }}
          >
            Esc to exit
          </Typography>
        </Box>
      </Popover>
    </>
  );
}

export default KeyboardShortcuts;
